/**
 * HeroSection Component
 * Main landing section with title, price calculator, and product showcase
 */

import React from 'react';
import { PriceCalculator } from './PriceCalculator';
import { PriceChange } from '../types/metalRates';

interface HeroSectionProps {
  goldRate: number | null;
  silverRate: number | null;
  goldChange: PriceChange | null;
  silverChange: PriceChange | null;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  goldRate,
  silverRate,
  goldChange,
  silverChange,
}) => {

  return (
    <section
      id="hero"
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center py-20 px-6 relative overflow-hidden"
    >
      {/* Background jewellery image - full coverage */}
      <div className="absolute inset-0 w-full h-full">
        <img 
          src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1920&h=1080&fit=crop" 
          alt="" 
          className="w-full h-full object-cover opacity-25 md:opacity-15"
        />
        {/* Dark overlay gradient for better text readability - balanced for mobile */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/85 md:bg-gradient-to-r md:from-black/90 md:via-black/70 md:to-black/60"></div>
      </div>

      {/* Additional decorative diamond on top right */}
      <div className="absolute top-10 right-10 w-64 h-64 opacity-10">
        <img 
          src="https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop" 
          alt="" 
          className="w-full h-full object-cover rounded-full blur-sm"
        />
      </div>

      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-5 gap-16 items-center relative z-10">
        {/* Left Side - Title and Rate Cards (60% width - 3 columns) */}
        <div className="lg:col-span-3 space-y-10 text-center lg:text-left">
          {/* Tagline */}
          <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase">
            Exclusivity Redefined
          </p>

          {/* Title with serif font */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif leading-tight">
            <span className="text-white block">Crafting</span>
            <span className="text-yellow-500 italic block">Timeless</span>
            <span className="text-white block">Masterpieces</span>
          </h1>

          {/* Rate Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6">
            {/* Gold Rate Card */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-lg p-6 backdrop-blur-sm text-center lg:text-left">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-3">Gold Rate Today</p>
              <p className="text-white text-4xl font-bold mb-2">
                ₹ {goldRate ? goldRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '15,998.00'}
              </p>
              {goldChange ? (
                <p className={`text-sm font-semibold flex items-center justify-center lg:justify-start ${goldChange.direction === 'up' ? 'text-green-500' : goldChange.direction === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                  <span className="mr-1">{goldChange.direction === 'up' ? '▲' : goldChange.direction === 'down' ? '▼' : '●'}</span>
                  {goldChange.direction !== 'neutral' && (goldChange.amount >= 0 ? '+' : '')}
                  {goldChange.percentage.toFixed(2)}%
                </p>
              ) : (
                <p className="text-sm font-semibold text-gray-500">N/A</p>
              )}
            </div>

            {/* Silver Rate Card */}
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-lg p-6 backdrop-blur-sm text-center lg:text-left">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-3">Silver Rate Today</p>
              <p className="text-white text-4xl font-bold mb-2">
                ₹ {silverRate ? silverRate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '302.52'}
              </p>
              {silverChange ? (
                <p className={`text-sm font-semibold flex items-center justify-center lg:justify-start ${silverChange.direction === 'up' ? 'text-green-500' : silverChange.direction === 'down' ? 'text-red-500' : 'text-gray-500'}`}>
                  <span className="mr-1">{silverChange.direction === 'up' ? '▲' : silverChange.direction === 'down' ? '▼' : '●'}</span>
                  {silverChange.direction !== 'neutral' && (silverChange.amount >= 0 ? '+' : '')}
                  {silverChange.percentage.toFixed(2)}%
                </p>
              ) : (
                <p className="text-sm font-semibold text-gray-500">N/A</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Price Calculator (40% width - 2 columns) */}
        <div className="lg:col-span-2 flex justify-center lg:justify-end">
          <PriceCalculator goldRate={goldRate} silverRate={silverRate} />
        </div>
      </div>
    </section>
  );
};
